import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Container, Skeleton } from '@mui/material';

import appsignal from '../../Services/ErrorReportingService';
import UiSection from '../../Components/UiSection';
import Header from '../../Components/Entities/Home/Header';
import MessagingBloc from '../../Components/MessagingBloc';
import SpaceStats from '../../Components/Entities/About/SpaceStats';
import Posts from '../../Components/PostList/Posts';
import { Types } from '../../Utils/Types/Posts';
import useSpace from '../../Hooks/useSpace';
import useApiQuery from '../../Hooks/useApiQuery';
import queriesConfig from '../../Services/queriesConfig';
import FoldersLayer from '../../Components/Entities/FoldersLayer';
import { getSectionBackgroundColor } from '../../Utils/UiSections';
import { Page } from '../../Utils/Types/UiSection';
import CommunicationListOfCards from './CommunicationListOfCards';
import ConsultationListOfCards from './ConsultationListOfCards';

function UiSectionSkeletonWrapper({ backgroundColor }: { backgroundColor: string }) {
  return (
    <Box sx={{ backgroundColor, wordBreak: 'break-word' }} py={8}>
      <Container maxWidth="md">
        <Skeleton variant="text" sx={{ fontSize: '3rem', mx: '20%' }} />
        <Skeleton variant="rounded" height={200} sx={{ mt: 2 }} />
      </Container>
    </Box>
  );
}

function ListOfUiSections({ page }: { page: Page }) {
  const { space } = useSpace();
  const theme = useTheme();

  const { data: uiSections = [], isLoading } = useApiQuery({
    query: queriesConfig.getUiSections(page, { enabled: true }),
  });

  if (isLoading) {
    return Array.from({ length: 3 }, (_, index) => (
      <UiSectionSkeletonWrapper
        key={index + 1}
        backgroundColor={getSectionBackgroundColor(theme, index + 1)}
      />
    ));
  }

  return uiSections.map((section, index) => {
    const componentName = section.component_name;
    const backgroundColor = getSectionBackgroundColor(theme, index);

    switch (componentName) {
      case 'anomalyForm':
        return (
          <UiSection key={section.id} backgroundColor={backgroundColor}>
            <MessagingBloc
              content={section.content}
              spaceSlug={space.slug}
              postType="anomaly"
              ctaLabel={section.cta_label}
              ctaUrl={section.cta_url}
            />
          </UiSection>
        );

      case 'customBlock':
        return <UiSection key={section.id} section={section} backgroundColor={backgroundColor} />;

      case 'demandForm':
        return (
          <UiSection key={section.id} backgroundColor={backgroundColor}>
            <MessagingBloc content={section?.content} spaceSlug={space.slug} postType="demand" />
          </UiSection>
        );

      case 'featuredConsultations':
        return (
          <UiSection key={section.id} section={section} backgroundColor={backgroundColor}>
            <ConsultationListOfCards space={space} uiSection={section} />
          </UiSection>
        );

      case 'featuredNews':
        return (
          <UiSection key={section.id} section={section} backgroundColor={backgroundColor}>
            <CommunicationListOfCards space={space} uiSection={section} />
          </UiSection>
        );

      case 'folders':
        return (
          <UiSection key={section.id} section={section} backgroundColor={backgroundColor}>
            <FoldersLayer
              space={space}
              parentId={section.post_record_id}
              postRecordIds={section.post_record_ids}
              filteringType={section.filtering_type}
            />
          </UiSection>
        );

      case 'kpis':
        return (
          <UiSection key={section.id} section={section} backgroundColor={backgroundColor}>
            <SpaceStats space={space} section={section} />
          </UiSection>
        );

      case 'latestIdeas':
        return (
          <UiSection key={section.id} section={section} backgroundColor={backgroundColor}>
            <Posts
              postsTypes={[Types.IDEA]}
              analyticsTitle="Home - Ideas"
              trackingProps={{ category: 'Home', name: 'Click on Ideas card' }}
              limit={5}
              hideButtons
            />
          </UiSection>
        );

      case 'spaceInfo':
        return <Header key={section.id} section={section} />;

      default:
        appsignal.sendError(new Error(`Component ${componentName} not found in ${page} page`));
        return null;
    }
  });
}

export default ListOfUiSections;
